import {
  FLOW_EFFECT_RPC_TIMEOUT_MS,
  type RobotCommandResult,
} from '@/connection/BatchRobotService';
import { abortableDelay } from '../../abortableDelay';
import type { Logger, NodeDefinition } from '../../types';
import { t, r } from '../../rubyParts';
import {
  DEFAULT_LIGHT_COLOR,
  getLightHexColor,
  getLightLabel,
  getLightPayload,
  isLightColor,
  LIGHT_EXECUTION_DURATION_MS,
} from '../../config/lightPresets';
import { LightNodeView } from '../views/LightNode';
import { LightSettings } from '../settings/LightSettings';

const logRobotResults = (log: Logger, results: RobotCommandResult[]) => {
  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      log(`[Light][${result.robotId}] 成功`);
      return;
    }
    log(`[Light][${result.robotId}] ${result.status}: ${result.message ?? result.errorCode ?? '不明なエラー'}`);
  });
};

export const lightDefinition: NodeDefinition<'light'> = {
  label: 'Light',
  showInPalette: true,
  paletteColor: 'bg-yellow-100 border-yellow-500 text-yellow-700',
  miniMapColor: '#eab308',
  defaultData: { label: 'Light', color: DEFAULT_LIGHT_COLOR },
  view: LightNodeView,
  settings: LightSettings,
  run: async (node, ctx, log) => {
    const color = node.data.color ?? DEFAULT_LIGHT_COLOR;
    if (!isLightColor(color)) {
      log('[Light] エラー: ライトの色を選び直してください');
      return { nextHandle: null };
    }
    const label = getLightLabel(color, 'ja');
    ctx.onExecutionEvent?.({ type: 'light', nodeId: node.id, color });
    if (!ctx.robotService) {
      ctx.setSimulationLightColor?.(getLightHexColor(color));
      log(`[Light][Simulation] ${label}`);
      try {
        await abortableDelay(LIGHT_EXECUTION_DURATION_MS, ctx.signal);
      } finally {
        ctx.setSimulationLightColor?.('#000000');
      }
      return;
    }

    try {
      const summary = await ctx.robotService.setLed(
        getLightPayload(color),
        { nodeId: node.id, signal: ctx.signal, timeoutMs: FLOW_EFFECT_RPC_TIMEOUT_MS },
      );
      logRobotResults(log, summary.results);
      const succeeded = summary.results.filter((result) => result.status === 'fulfilled').length;
      log(`[Light] ${label}（${succeeded}/${summary.results.length}台）`);
      await abortableDelay(LIGHT_EXECUTION_DURATION_MS, ctx.signal);
    } finally {
      if (ctx.robotService.cleanupLed) {
        await ctx.robotService.cleanupLed({
          nodeId: `${node.id}:cleanup`,
          timeoutMs: FLOW_EFFECT_RPC_TIMEOUT_MS,
        });
      } else {
        await ctx.robotService.setLed(
          { red: 0, green: 0, blue: 0, mode: 0 },
          { nodeId: `${node.id}:cleanup`, timeoutMs: FLOW_EFFECT_RPC_TIMEOUT_MS },
        );
      }
    }
  },
  help: {
    title: [t('Light（ライト）')],
    description: [
      t('ロボットのライトを、'),
      r('選', 'えら'),
      t('んだ'),
      r('色', 'いろ'),
      t('で'),
      r('光', 'ひか'),
      t('らせます。カードの'),
      r('実行', 'じっこう'),
      t('が'),
      r('終', 'お'),
      t('わるとライトは'),
      r('自動', 'じどう'),
      t('で'),
      r('消', 'き'),
      t('えます。'),
    ],
  },
};
